import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../store';
import { settingsApi } from '../services/api';
import type { NotionDatabase } from '../types';

export default function SetupPage() {
  const navigate = useNavigate();
  const { user, setUser } = useAppStore();
  const [databases, setDatabases] = useState<NotionDatabase[]>([]);
  const [selectedDb, setSelectedDb] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  
  useEffect(() => {
    loadDatabases();
  }, []);
  
  const loadDatabases = async () => {
    try {
      const response = await settingsApi.listNotionDatabases();
      if (response.success && response.data) {
        setDatabases(response.data);
      }
    } catch (err) {
      console.error('Failed to load databases:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCreateDatabase = async () => {
    setIsCreating(true);
    setError('');
    
    try {
      const response = await settingsApi.createOceanDatabase();
      if (response.success && response.data && user) {
        setUser({
          ...user,
          notionTaskDatabaseId: response.data.databaseId,
        });
        navigate('/tasks');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create database');
    } finally {
      setIsCreating(false);
    }
  };
  
  const handleSelectDatabase = async () => {
    if (!selectedDb) return;
    
    setIsSaving(true);
    setError('');
    
    try {
      const response = await settingsApi.setNotionDatabase(selectedDb);
      if (response.success && response.data && user) {
        setUser({
          ...user,
          notionTaskDatabaseId: response.data.databaseId,
        });
        navigate('/tasks');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set database');
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ocean-50 via-white to-slate-100 p-4">
      <div className="max-w-lg w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8 animate-fade-in">
          <div className="text-center mb-8">
            <img src="/ocean.svg" alt="Ocean" className="w-12 h-12 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Set Up Ocean</h1>
            <p className="text-slate-600">
              Choose where to store your tasks in Notion
            </p>
          </div>
          
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          {isLoading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-ocean-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-600">Loading your Notion databases...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Option 1: Create new database */}
              <div className="p-4 border-2 border-ocean-200 bg-ocean-50 rounded-xl">
                <h3 className="font-medium text-slate-900 mb-2">
                  Recommended: Create Ocean Tasks Database
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  We'll create a new database with all the properties Ocean needs
                </p>
                <button
                  onClick={handleCreateDatabase}
                  disabled={isCreating}
                  className="w-full px-4 py-2 bg-ocean-600 text-white rounded-lg hover:bg-ocean-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isCreating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Create Ocean Tasks Database
                    </>
                  )}
                </button>
              </div>
              
              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-slate-500">or</span>
                </div>
              </div>
              
              {/* Option 2: Select existing database */}
              <div className="p-4 border border-slate-200 rounded-xl">
                <h3 className="font-medium text-slate-900 mb-2">
                  Use Existing Database
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Select a database that Ocean has access to
                </p>
                
                {databases.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">
                    No databases found. Make sure you've shared at least one database with Ocean in Notion.
                  </p>
                ) : (
                  <>
                    <select
                      value={selectedDb}
                      onChange={(e) => setSelectedDb(e.target.value)}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-ocean-500 focus:border-transparent mb-4"
                    >
                      <option value="">Select a database...</option>
                      {databases.map((db) => (
                        <option key={db.id} value={db.id}>
                          {db.title}
                        </option>
                      ))}
                    </select>
                    
                    <button
                      onClick={handleSelectDatabase}
                      disabled={!selectedDb || isSaving}
                      className="w-full px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                    >
                      {isSaving ? 'Saving...' : 'Use This Database'}
                    </button>
                  </>
                )}
              </div>
              
              {/* Help text */}
              <p className="text-xs text-slate-500 text-center">
                Don't see your database? Make sure you've shared it with Ocean in Notion's share menu.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


